import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { MenuItem } from '@openng/optimus-ui/api';
import { combineLatest, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { AuthorizationService } from '~ngx-shared/authentication';
import { MenuService } from '~ngx-shared/layout';
import { Aggregate } from '~ngx-shared/models';
import { UserService } from '~shared/services';
import { RoleEnum } from '~shared/utils';

@Injectable({
  providedIn: 'root'
})
export class FarabiService {
  private readonly destroyRef = inject(DestroyRef);
  subscriptions: Subscription[] = [];
  changeMenuItem = new Subject<MenuItem>();

  private readonly authorizationService = inject(AuthorizationService);
  private readonly userService = inject(UserService);
  private readonly menuService = inject(MenuService);
  private readonly apollo = inject(Apollo);

  constructor() {
    this.destroyRef.onDestroy(() => this.subscriptions.forEach(s => s.unsubscribe()));
    this.changeMenuItem.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(item => {
      const menuItem = this.menuService.getItemFromRoute(item.routerLink);
      if (!menuItem) return;

      menuItem.badge = Number(item.badge || 0) > 0 ? item.badge : undefined;
    });
  }

  init(): void {
    combineLatest([
      this.userService.user$.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()),
      this.authorizationService.currentRole$.pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged()
      )
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, role]) => {
        // Unsubscribe to clear old ones
        this.subscriptions.forEach(subscription => subscription.unsubscribe());

        if (
          this.authorizationService.can(RoleEnum.MODERATOR) ||
          (user && role && role === RoleEnum.COMMUNITY_OFFICE)
        ) {
          const farabiMenuItem = this.menuService.getItemFromIds(['farabi']);

          if (farabiMenuItem) {
            farabiMenuItem.visible =
              user?.current_person_data?.can_sign ||
              this.authorizationService.can(RoleEnum.MODERATOR) ||
              false;
          }

          const personId = user?.person_id || 0;

          this.subscriptions.push(
            this.apollo
              .subscribe<{ result?: Aggregate }>({
                query: gql`
                    subscription GetEducationTerminationOfServiceApproval($personId: bigint!) {
                      result: education_termination_of_service_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            termination_of_service_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                variables: {
                  personId
                }
              })
              .subscribe(queryResult => {
                this.changeMenuItem.next({
                  id: 'termination_of_service',
                  label: 'shared.termination_of_services',
                  icon: 'pi pi-fw pi-briefcase',
                  routerLink: ['/', 'farabi', 'termination-of-services'],
                  badge: String(queryResult.data?.result?.aggregate?.count),
                  state: {
                    count: queryResult.data?.result?.aggregate?.count
                  }
                });
              }),
            this.apollo
              .subscribe<{ result?: Aggregate }>({
                query: gql`
                    subscription GetEducationDeclarationOfCommitmentIlPdApproval($personId: bigint!) {
                      result: education_declaration_of_commitment_il_pd_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            declaration_of_commitment_il_pd_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                variables: {
                  personId
                }
              })
              .subscribe(queryResult => {
                this.changeMenuItem.next({
                  id: 'declaration_of_commitment_il_pd',
                  label: 'shared.declaration_of_commitment_il_pds',
                  icon: 'pi pi-fw pi-briefcase',
                  routerLink: ['/', 'farabi', 'declaration-of-commitment-il-pds'],
                  badge: String(queryResult.data?.result?.aggregate?.count),
                  state: {
                    count: queryResult.data?.result?.aggregate?.count
                  }
                });
              }),
            this.apollo
              .subscribe<{ result?: Aggregate }>({
                query: gql`
                    subscription GetEducationIgazaCertificateApproval($personId: bigint!) {
                      result: education_igaza_certificate_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            igaza_certificate_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                variables: {
                  personId
                }
              })
              .subscribe(queryResult => {
                this.changeMenuItem.next({
                  id: 'igaza_certificate',
                  label: 'shared.igaza_certificates',
                  icon: 'pi pi-fw pi-briefcase',
                  routerLink: ['/', 'farabi', 'igaza-certificates'],
                  badge: String(queryResult.data?.result?.aggregate?.count),
                  state: {
                    count: queryResult.data?.result?.aggregate?.count
                  }
                });
              }),
            this.apollo
              .subscribe<{ result?: Aggregate }>({
                query: gql`
                    subscription GetEducationServiceContractApproval($personId: bigint!) {
                      result: education_service_contract_aggregate(
                        where: {
                          approval_status: { _eq: pending }
                          _not: {
                            service_contract_approvals: {
                              person_id: { _eq: $personId }
                               role_name: { _eq: "${role}" }
                            }
                          }
                        }
                      ) {
                        aggregate {
                          count
                        }
                      }
                    }
                  `,
                variables: {
                  personId
                }
              })
              .subscribe(queryResult => {
                this.changeMenuItem.next({
                  id: 'service_contract',
                  label: 'shared.service_contracts',
                  icon: 'pi pi-fw pi-briefcase',
                  routerLink: ['/', 'farabi', 'service-contracts'],
                  badge: String(queryResult.data?.result?.aggregate?.count),
                  state: {
                    count: queryResult.data?.result?.aggregate?.count
                  }
                });
              })
          );
        }
      });
  }
}
